<template>
    <div class="my-4 d-flex align-items-center justify-content-center">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div class="w-100">
            
            <div v-if="isActive">

                <div v-if="!hasNodeDetail" key="loadingNodeDetail" class="d-flex align-items-center justify-content-center min-height-100">
                    <h5 class="text-muted">Loading...</h5>
                </div>
                
                <div v-else-if="anyApprovalTriggersAvailable" key="approvalButtons">
                    
                    <div class="" :class="hasApprovalForm ? '' : 'max-width-500 mx-auto'">
                        <h5>{{ title }}</h5>
                        <p class="text-muted">{{ nodeData.approval_config.instructions }}</p>
                    </div>

                    <div v-if="hasApprovalForm" class=""><!-- approval form -- if any! -->
                        <FormulateForm
                            ref="approvalForm"
                            name="approvalForm"
                            :debounce="100"
                            :values="approvalFormInitialData"
                            :schema="approvalFormSchema"
                            class="mt-4"
                            :class="approvalFormCssClasses"
                            @created="handleFormCreated"
                            @validation="handleFormValidation"
                            @blur="handleFormBlur"
                            @input="handleFormInput"
                            @submit="handleFormSubmit"
                        ></FormulateForm>
                    </div>
                    
                    <div class="max-width-700 mx-auto mt-4 d-flex justify-content-around align-items-center">
                        <b-button
                            size="lg"
                            variant="sharp-danger"
                            class="min-width-200"
                            :disabled="!rejectTriggerAvailable || loadingTransition"
                            @click="startReject"
                        >   
                            <b-spinner v-if="loadingTransition" key="rejectLoading" small></b-spinner>
                            <div v-else key="showReject">
                                <span>{{nodeData.approval_config.reject_choice.label}}</span>
                                <b-icon icon="x-circle" class="ml-2"></b-icon>
                            </div>
                        </b-button>
                        <b-button
                            size="lg"
                            variant="sharp-success"
                            class="min-width-200"
                            :disabled="!approveTriggerAvailable || loadingTransition"
                            @click="startApprove"
                        >   
                            <b-spinner v-if="loadingTransition" key="approveLoading" small></b-spinner>
                            <div v-else key="showApprove">
                                <span>{{nodeData.approval_config.approve_choice.label}}</span>
                                <b-icon icon="check-circle" class="ml-2"></b-icon>
                            </div>
                        </b-button>
                    </div>

                </div>
                
                <div v-else key="noApprovalButtons" class="d-flex align-items-center justify-content-center min-height-100">
                    <h5 class="text-muted">Waiting on approval by assignee(s)</h5>
                </div>
            
            </div>
            <div v-else-if="isCompleted" class="d-flex align-items-center justify-content-center min-height-200">
                <div class="">
                    <h5 class="text-muted text-center mb-3">Approval Completed</h5>
                    <!-- add completed by -->
                    <div 
                        v-if="approvalResult" 
                        class="alert"
                        :class="`alert-${approvalResult.variant}`"
                    >
                        <p class="mb-0">
                            <span v-if="approvalResult.wasApproved" key="approveTitle" class="font-weight-bold">Approved</span>
                            <span v-else-if="approvalResult.wasRejected" key="rejectTitle" class="font-weight-bold">Rejected</span>
                            <span v-else key="otherApprovalTitle" class="font-weight-bold">Approval Status</span>
                            <span class="font-italic">
                                {{ approvalResult.byName ? ` by ${approvalResult.byName} ` : '' }} on {{ approvalResult.onFormatted }}
                            </span>
                        </p>
                        <hr v-if="approvalResult.message" class="my-2">
                        <div v-if="approvalResult.message" class="mb-0 text-show-breaks">{{ approvalResult.message }}</div>
                    </div>
                </div>
                <!-- <pre>{{ approvalResult }}</pre> -->
            </div>
            <div v-else-if="isWaiting" class="d-flex align-items-center justify-content-center min-height-200">
                <h5 class="text-muted">Waiting on other tasks</h5>
            </div>
        </div>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import FlowNodeContentMixin from './FlowNodeContentMixin';
import { FormatDateTimeLocal } from '../../js/filters-helpers';

export default {
    name: 'FlowNodeApproval',
    components: {},
    mixins: [
        errorHandling,
        FlowNodeContentMixin,
    ],
    props: {  // FROM MIXIN
        /* nodeData: {  
            type: Object,
            required: true,
        },
        nodeContext: {  // node-level context, with info/methods on calling transitions
            type: Object,
            required: true,  
        }, */
    },
    data() {
        return {
            errorMsg: 'Error',
            error: false,
            approveTriggerName: 'approve',
            rejectTriggerName: 'reject',
        }
    },
    computed: {
        approveTriggerAvailable() {
            return !!this.nodeContext.nodeTriggers && Array.isArray(this.nodeContext.nodeTriggers) && this.nodeContext.nodeTriggers.includes(this.approveTriggerName);
        },
        rejectTriggerAvailable() {
            return !!this.nodeContext.nodeTriggers && Array.isArray(this.nodeContext.nodeTriggers) && this.nodeContext.nodeTriggers.includes(this.rejectTriggerName);
        },
        anyApprovalTriggersAvailable() {
            return this.approveTriggerAvailable || this.rejectTriggerAvailable;
        },
        approvalResult() {
            // if already completed, this is the FlowEvent for most recent approve/reject
            const rawResult = this.nodeData?.approval_result;  // lots of stuff in there... pull out the stuff we need 
            if (!rawResult) {
                return null;
            }
            const wasApproved = rawResult.trigger === 'approve';
            const wasRejected = rawResult.trigger === 'reject';
            return {
                wasApproved: wasApproved,
                wasRejected: wasRejected,
                variant: wasApproved ? 'success' : (wasRejected ? 'danger' : 'secondary'),  // just in case we have something weird
                byName: rawResult.tenant_user?.full_name ?? '',
                onFormatted: rawResult.created ? FormatDateTimeLocal(rawResult.created) : '',
                message: rawResult.event_kwargs?.message ?? '',
            };
        },
        // FORMS
        approvalFormComponent() {
            return this.$refs['approvalForm'] ?? null;
        },
        approvalForm() {
            return this.nodeData?.approval_config?.form?.form ?? {};
        },
        hasApprovalForm() {
            return !!this.approvalForm.key;
        },
        approvalFormKey() {
            return this.approvalForm.key ?? '';
        },
        approvalFormSchema() {
            return this.approvalForm.items ?? [];
        },
        approvalFormInitialData() {
            return this.nodeData?.approval_config?.form?.data ?? {};
        },
        approvalFormCssClasses() {
            return this.approvalForm.css_classes || [];
        },
        // many other computed() from mixin:  hasNodeDetail, isAssignee, isActive, isCompleted, isWaiting, title, loadingTransition, ...
    },
    mounted() {
        
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async startApprove() {
            await this.startApproveOrReject(this.approveTriggerName);
            //this.nodeContext.startTransition(this.approveTriggerName, formData);  // opens modal to confirm
        },
        async startReject() {
            await this.startApproveOrReject(this.rejectTriggerName);
            //this.nodeContext.startTransition(this.rejectTriggerName, formData);  // opens modal to confirm
        },
        async startApproveOrReject(triggerName) {
            // complete it - call transition from parent passed down?
            // July 6 2024 -- this 'top-level' approval form is for READ-ONLY THINGS, not for submitting
            // use approve_form and reject_form for submitting data
            /* const canProceed = await this.checkApprovalFormValid();
            if (!canProceed) {
                console.log('approval form not valid');
                return;  // for should be showing validation errors, right?
            }
            let formData = {};
            try {   
                formData = await this.getFormData();
            } catch (error) {
                console.log('form data is not valid, not proceeding with approve');
                return;  // show toast?
            } */
            const transitionData = {};
            // add the transition data nested in the key of the form? backend will expect it in certain structure
            // NO, NOT TOP-LEVEL FORM -- that is read-only:  transitionData[this.approvalFormKey] = formData;
            // TODO - show approve/reject forms inline, NOT in modal? and add to transitionData! then we'll have to call transition directly, not through startTransition
            let callDirectly = false;
            this.nodeContext.startTransition(triggerName, transitionData, callDirectly);  // opens modal to confirm - unless you pass 3rd arg as true (callDirectly)
        },
        // FORMS
        // NOTE - there are currently 2 different forms for approvals... the default approve/reject transition form (message) and the optional form... that's messy....
        handleFormCreated(payload) {
            console.log('handleFormCreated', payload);
        },
        handleFormValidation(payload) {
            console.log('handleFormValidation', payload);
        },
        handleFormBlur(payload) {
            console.log('handleFormBlur', payload);
        },
        handleFormInput(payload) {
            console.log('handleFormInput', payload);
        },
        async handleFormSubmit(formData) {
            console.log('handleFormSubmit', formData);
        },
        async checkApprovalFormValid() {
            //return await this.$refs.theForm.formSubmitted();
            const theForm = this.approvalFormComponent;   // might be null if no form 
            if (!theForm) {  // if no form, then no validation errors
                return true;
            }
            const hasErrors = await theForm.hasValidationErrors();
            return !hasErrors;
        },
        async getFormData() {
            if (!this.hasApprovalForm) {
                console.log('no approval form');
                return {};
            }
            const theForm = this.approvalFormComponent;   // might be null if no form 
            if (!theForm) {  // if no form, then no validation errors
                console.log('no approval form component found');
                return {};
            }
            const submitObj = await theForm.formSubmitted();
            // that returns false if something is not valid, otherwise returns object with data 
            // raise exception if you get false here 
            console.log('submitObj for approval form', submitObj);
            if (submitObj === false) {
                throw new Error('Approval form not valid');
            }
            return submitObj;
        }
    }
}
</script>

<style>

</style>